import { EnterElement, ScaleLinear, Selection, axisBottom } from "d3"
import { MarginedBoundingBox } from "../../Types/MarginedBoundingBox"
import { D3OneToOneRenderable } from "./D3OneToOneRenderable"
import { ReactCallbacks } from "../../Types/ReactCallbacks"
import { MobergFontFamily, MobergFontSize } from "../../../../../../Moberg"
import { AXIS_TICK_FONT_SIZE } from "../Visualizations/Constants"

export type D3LinearXAxisConfig = {
    boundingBox: MarginedBoundingBox
    scale: ScaleLinear<any, any>
    label?: string
}

export class D3LinearXAxis extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3LinearXAxisConfig> {
    private axisClassName: string = "d3-histogram-x-axis"
    private labelClassName: string = "d3-histogram-x-axis-label"

    constructor(root: SVGGElement, config: D3LinearXAxisConfig, reactCallbacks: ReactCallbacks<any>) {
        super(root, config, "d3-histogram-x-axis-container", reactCallbacks)
        this.render()
    }

    protected enter(newElements: Selection<EnterElement, D3LinearXAxisConfig, any, any>): Selection<SVGGElement, D3LinearXAxisConfig, SVGGElement, any> {
        // Create the Axis Group
        const axisGroup = newElements
            .append("g")
            .attr("class", this.className)
            .attr("transform", `translate(0, ${this.config.boundingBox.height})`)

        // Calculate tick values if necessary
        const axis = axisBottom(this.config.scale)

        axisGroup.append("g")
            .attr("class", this.axisClassName)
            .call(axis)
            .style("user-select", "none") // disables highlighting the ticks
            .style("font-size", AXIS_TICK_FONT_SIZE)
            .style("font-family", MobergFontFamily.REGULAR)
            .style("text-anchor", "middle")

        // create the label
        axisGroup.append("text")
            .attr("text-anchor", "middle")
            .attr("class", this.labelClassName)
            .attr("transform", `translate(${this.config.scale.range()[1] / 2}, 40)`)
            .text(() => this.config.label ?? "")

        return axisGroup
    }

    protected update(updatedElements: Selection<SVGGElement, D3LinearXAxisConfig, any, any>): Selection<SVGGElement, D3LinearXAxisConfig, SVGGElement, any> {
        const axisGroup = updatedElements.attr("transform", `translate(0, ${this.config.boundingBox.height})`)

        // Redraw the axis
        const axis = axisBottom(this.config.scale)
        const d3Axis = axisGroup.select("." + this.axisClassName) as Selection<SVGGElement, any, any, any>
        d3Axis.call(axis)

        // Update the label
        axisGroup.select("." + this.labelClassName)
            .attr("transform", `translate(${this.config.scale.range()[1] / 2}, 40)`)
            .text(() => this.config.label ?? "")
            .style("font-size", MobergFontSize.LARGE)
            .style("font-family", MobergFontFamily.REGULAR)

        return updatedElements
    }
}
